import React, { useState, useEffect, useCallback } from 'react';
import apiService, {
  getPathwayAssignmentData, // New API function
  updateClientAssignedPathway, // New API function
  // Keep updateClientTherapySettings if other settings are still managed by it
  updateClientTherapySettings,
  // New persona management functions
  updateClientUiOptions,
  updateClientAiPersona,
  startNewConversationForClient,
  getTherapistPersonas,
  getPlatformSettings,
  // Phase transition functions
  getClientPathway,
  evaluatePhaseReadiness,
  approvePhaseTransition,
  rejectPhaseTransition,
  attestReadinessCriterion
} from '../../services/api';
import axios from 'axios';
import ClientVoiceOverrides from './ClientVoiceOverrides'; // Import enhanced voice component

// MUI Imports
import ListSubheader from '@mui/material/ListSubheader'; // For dropdown categories
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
// MUI Lab date pickers were causing compilation errors - using fallback date picker instead
// import DatePicker from '@mui/lab/DatePicker';
// import AdapterDateFns from '@mui/lab/AdapterDateFns';
// import LocalizationProvider from '@mui/lab/LocalizationProvider';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningIcon from '@mui/icons-material/Warning';
import LockIcon from '@mui/icons-material/Lock';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// Create a "fallback" date picker component if MUI Lab components aren't available
// This can be removed once the proper date picker is installed
const FallbackDatePicker = ({ label, value, onChange, disabled }) => {
  // Convert date object to YYYY-MM-DD string for input
  const dateValue = value ? new Date(value).toISOString().split('T')[0] : '';
  
  return (
    <TextField
      label={label}
      type="date"
      value={dateValue}
      onChange={e => {
        const date = e.target.value ? new Date(e.target.value) : null;
        onChange(date);
      }}
      disabled={disabled}
      InputLabelProps={{ shrink: true }}
      fullWidth
      margin="normal"
    />
  );
};

const ClientTherapySettings = ({ clientId, token, clientName }) => {
  // State for pathway assignment
  const [recommendedPathways, setRecommendedPathways] = useState([]);
  const [otherPathways, setOtherPathways] = useState([]);
  const [currentAssignedPathwayDetails, setCurrentAssignedPathwayDetails] = useState(null); // For visualization

  // State for AI personas
  const [therapistPersonas, setTherapistPersonas] = useState([]);
  
  // Combined client settings state
  const [clientSettings, setClientSettings] = useState({
    sessionMode: 'continuous',
    sessionLength: null, // Will use platform default initially
    sessionFrequency: null, // Will use platform default initially
    enforceTimeBoundaries: null, // Will use platform default initially
    // Pathway related fields will be populated from API
    assignedPathwayId: null,
    assignedPathwayName: null,
    currentPhase: null, // Stores phase name
    phaseStartDate: null,
    customReflectionFrequency: null, // Will use platform default initially
    // Other fields like nextScheduledSession, lastSessionDate if managed here
    
    // Client UI options
    clientUiOptions: {
      showPersonaSelector: false,
      showNewChatButton: false,
      assignedPersonaKey: 'generalist'
    }
  });
  
  // State for persona management
  const [selectedPersonaKey, setSelectedPersonaKey] = useState('generalist');
  const [startNewConversation, setStartNewConversation] = useState(false);
  const [personaSaving, setPersonaSaving] = useState(false); // Separate saving state for persona actions
  
  // Voice settings are now handled by ClientVoiceOverrides component

  // State for non-pathway platform defaults (if still needed for display)
  const [platformDefaults, setPlatformDefaults] = useState({
    defaultSessionLength: 45,
    defaultSessionFrequency: 'weekly',
    defaultEnforceTimeBoundaries: false,
    defaultReflectionFrequency: 'weekly',
  });


  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Selected values for dropdowns - local to UI interaction before saving
  const [selectedPathwayId, setSelectedPathwayId] = useState('');
  const [selectedPathwayName, setSelectedPathwayName] = useState('');
  const [selectedPhaseName, setSelectedPhaseName] = useState('');

  // Phase transition state
  const [pathwayInfo, setPathwayInfo] = useState(null);
  const [readinessEvaluation, setReadinessEvaluation] = useState(null);
  const [loadingTransition, setLoadingTransition] = useState(false);
  const [transitionDialogOpen, setTransitionDialogOpen] = useState(false);
  const [transitionAction, setTransitionAction] = useState(null); // 'approve' or 'reject'
  const [transitionNotes, setTransitionNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');


  // Load pathway assignment data and existing client settings
  const fetchPathwayAndClientSettings = useCallback(async () => {
    if (!clientId || !token) return;
    setLoading(true);
    setError('');
    try {
      const data = await getPathwayAssignmentData(clientId, token);
      setRecommendedPathways(data.recommendedPathways || []);
      setOtherPathways(data.otherPathways || []);
      
      // Initialize clientSettings with all fetched or default values
      // This part needs to merge existing client settings (non-pathway) with pathway settings
      // For now, focusing on pathway assignment part from the new endpoint
      // A more robust approach would fetch general client settings separately if they exist
      // or the new endpoint should return all relevant client therapy settings.
      // Assuming getPathwayAssignmentData returns all necessary clientSettings fields for now.

      // Fetch general client therapy settings using the GET endpoint
      const fetchedGeneralSettings = await apiService.getClientTherapySettings(clientId, token);
      
      setClientSettings(prev => ({
        ...prev, // Keep existing defaults for non-pathway fields from useState
        ...fetchedGeneralSettings, // Override with fetched general settings
        assignedPathwayId: data.currentAssignedPathwayId || null, // Pathway data from getPathwayAssignmentData
        assignedPathwayName: data.currentAssignedPathwayName || null,
        currentPhase: data.currentAssignedPhaseName || null,
        // Ensure phaseStartDate from fetchedGeneralSettings is prioritized if it exists
        phaseStartDate: fetchedGeneralSettings.phaseStartDate || data.currentAssignedPathwayDetails?.phases.find(p => p.name === data.currentAssignedPhaseName)?.startDate || (data.currentAssignedPhaseName ? new Date() : null)
      }));

      setSelectedPathwayId(data.currentAssignedPathwayId || '');
      setSelectedPathwayName(data.currentAssignedPathwayName || '');
      setSelectedPhaseName(data.currentAssignedPhaseName || '');
      setCurrentAssignedPathwayDetails(data.currentAssignedPathwayDetails || null);

      // Fetch general platform settings for defaults if needed for other UI elements
      // This part can be optimized if the new endpoint provides all necessary defaults
      // For now, keeping a simplified default structure
      // const platformData = await getPlatformSettings(token); // This might be redundant if new endpoint is comprehensive
      // setPlatformDefaults({
      //   defaultSessionLength: platformData?.sessionStructure?.defaultSessionLength || 45,
      //   // ... other defaults
      // });

    } catch (err) {
      console.error('Error fetching pathway and client settings:', err);
      setError('Failed to load therapy settings data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [clientId, token]);

  // Fetch therapist personas
  const fetchTherapistPersonas = useCallback(async () => {
    try {
      const personas = await getTherapistPersonas(token);
      setTherapistPersonas(personas || []);
    } catch (err) {
      console.error('Error fetching therapist personas:', err);
      // Don't set error state here as it's not critical
    }
  }, [token]);

  // Fetch pathway and readiness info for phase transitions
  const fetchPathwayInfo = useCallback(async () => {
    if (!clientId || !token) return;
    try {
      const data = await getClientPathway(clientId, token);
      if (data.success) {
        setPathwayInfo(data);
      }
    } catch (err) {
      console.error('Error fetching pathway info:', err);
      // Non-critical - don't set error state
    }
  }, [clientId, token]);

  // Fetch readiness evaluation
  const fetchReadinessEvaluation = useCallback(async () => {
    if (!clientId || !token || !pathwayInfo?.pathway) return;
    try {
      const data = await evaluatePhaseReadiness(clientId, token);
      if (data.success) {
        setReadinessEvaluation(data.evaluation);
      }
    } catch (err) {
      console.error('Error fetching readiness evaluation:', err);
    }
  }, [clientId, token, pathwayInfo?.pathway]);

  // Initial data loading
  useEffect(() => {
    fetchPathwayAndClientSettings();
    fetchTherapistPersonas();
    fetchPathwayInfo();
  }, [fetchPathwayAndClientSettings, fetchTherapistPersonas, fetchPathwayInfo]);

  // Fetch readiness when pathway info is available
  useEffect(() => {
    if (pathwayInfo?.pathway) {
      fetchReadinessEvaluation();
    }
  }, [pathwayInfo, fetchReadinessEvaluation]);
  
  // Update selected persona key when client settings change
  useEffect(() => {
    if (clientSettings.clientUiOptions?.assignedPersonaKey) {
      setSelectedPersonaKey(clientSettings.clientUiOptions.assignedPersonaKey);
    }
  }, [clientSettings.clientUiOptions?.assignedPersonaKey]);


  const handleSaveSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    // Consolidate all settings to be saved
    const settingsToUpdate = {
      ...clientSettings, // Includes sessionMode, sessionLength etc.
      assignedPathwayId: selectedPathwayId || null,
      assignedPathwayName: selectedPathwayId ? selectedPathwayName : null, // Only save name if ID is present
      currentPhase: selectedPathwayId && selectedPhaseName ? selectedPhaseName : null, // Only save phase if pathway is set
      phaseStartDate: selectedPathwayId && selectedPhaseName ? (clientSettings.phaseStartDate || new Date()) : null
    };
    
    try {
      // Use updateClientTherapySettings to save the general settings
      // And use updateClientAssignedPathway for specific pathway changes
      // For simplicity now, assuming updateClientTherapySettings can handle all these fields
      // or we make two calls. Let's try one call first.
      // The backend for updateClientTherapySettings would need to be robust enough.
      // OR, we make a specific call for pathway, and another for other settings if they changed.

      // Let's assume updateClientTherapySettings is the main function for now
      // and it's updated on the backend to handle these new pathway fields.
      // If not, we'd use updateClientAssignedPathway for pathway part.
      
      await updateClientTherapySettings(clientId, settingsToUpdate, token);

      setSuccess('Client therapy settings updated successfully!');
      // Refresh data to show updated state, especially for visualization
      fetchPathwayAndClientSettings(); 
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving client therapy settings:', err);
      setError(err.message || 'Failed to update therapy settings');
    } finally {
      setSaving(false);
    }
  };

  // Handles changes for general settings like sessionMode, sessionLength etc.
  const handleGeneralSettingChange = (field, value) => {
    setClientSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle UI option changes (persona selector visibility, new chat button visibility)
  const handleUiOptionChange = (field, value) => {
    setClientSettings(prev => ({
      ...prev,
      clientUiOptions: {
        ...prev.clientUiOptions,
        [field]: value
      }
    }));
  };

  // Save UI options to server
  const handleSaveUiOptions = async () => {
    setPersonaSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const options = {
        showPersonaSelector: !!clientSettings.clientUiOptions?.showPersonaSelector,
        showNewChatButton: !!clientSettings.clientUiOptions?.showNewChatButton
      };
      
      await updateClientUiOptions(clientId, options, token);
      
      setSuccess('Client UI options updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating client UI options:', err);
      setError(err.message || 'Failed to update client UI options');
    } finally {
      setPersonaSaving(false);
    }
  };

  // Apply persona change to client
  const handleApplyPersonaChange = async () => {
    setPersonaSaving(true);
    setError('');
    setSuccess('');
    
    try {
      // Get current persona name for success message
      const persona = therapistPersonas.find(p => p.key === selectedPersonaKey) || { name: selectedPersonaKey };
      
      if (startNewConversation) {
        // Start a new conversation with the selected persona
        await startNewConversationForClient(clientId, selectedPersonaKey, token);
        setSuccess(`Started new conversation with ${persona.name} AI Therapist!`);
      } else {
        // Update current conversation with new persona
        await updateClientAiPersona(clientId, { 
          personaKey: selectedPersonaKey,
          startNewConversation: false
        }, token);
        setSuccess(`Updated client's AI persona to ${persona.name}`);
      }
      
      // Update client settings with the new persona
      setClientSettings(prev => ({
        ...prev,
        clientUiOptions: {
          ...prev.clientUiOptions,
          assignedPersonaKey: selectedPersonaKey
        }
      }));
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating client AI persona:', err);
      setError(err.message || 'Failed to update client AI persona');
    } finally {
      setPersonaSaving(false);
    }
  };

  // Phase transition handlers
  const handleOpenTransitionDialog = (action) => {
    setTransitionAction(action);
    setTransitionNotes('');
    setRejectReason('');
    setTransitionDialogOpen(true);
  };

  const handleCloseTransitionDialog = () => {
    setTransitionDialogOpen(false);
    setTransitionAction(null);
    setTransitionNotes('');
    setRejectReason('');
  };

  const handleSubmitTransition = async () => {
    setLoadingTransition(true);
    setError('');
    setSuccess('');

    try {
      if (transitionAction === 'approve') {
        await approvePhaseTransition(clientId, transitionNotes, token);
        setSuccess('Phase transition approved successfully!');
      } else if (transitionAction === 'reject') {
        await rejectPhaseTransition(clientId, rejectReason, transitionNotes, token);
        setSuccess('Phase transition request rejected.');
      }

      handleCloseTransitionDialog();
      // Refresh data
      fetchPathwayAndClientSettings();
      fetchPathwayInfo();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error processing phase transition:', err);
      setError(err.message || 'Failed to process phase transition');
    } finally {
      setLoadingTransition(false);
    }
  };

  const handleAttestCriterion = async (criterionId) => {
    setLoadingTransition(true);
    try {
      await attestReadinessCriterion(clientId, criterionId, token);
      setSuccess('Criterion attested successfully!');
      fetchReadinessEvaluation();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error attesting criterion:', err);
      setError(err.message || 'Failed to attest criterion');
    } finally {
      setLoadingTransition(false);
    }
  };

  const handlePathwaySelectionChange = (event) => {
    const newPathwayId = event.target.value;
    setSelectedPathwayId(newPathwayId);

    if (newPathwayId) {
      const allPathways = [...recommendedPathways, ...otherPathways];
      const pathway = allPathways.find(p => p._id === newPathwayId);
      setSelectedPathwayName(pathway ? pathway.name : '');
      // Reset phase when pathway changes
      setSelectedPhaseName(''); 
      setClientSettings(prev => ({
        ...prev,
        assignedPathwayId: newPathwayId,
        assignedPathwayName: pathway ? pathway.name : null,
        currentPhase: null, // Reset phase
        phaseStartDate: null
      }));
    } else {
      setSelectedPathwayName('');
      setSelectedPhaseName('');
      setClientSettings(prev => ({
        ...prev,
        assignedPathwayId: null,
        assignedPathwayName: null,
        currentPhase: null,
        phaseStartDate: null
      }));
    }
  };

  const handlePhaseSelectionChange = (event) => {
    const newPhaseName = event.target.value;
    setSelectedPhaseName(newPhaseName);
    setClientSettings(prev => ({
      ...prev,
      currentPhase: newPhaseName || null,
      phaseStartDate: newPhaseName ? (prev.phaseStartDate && prev.currentPhase === newPhaseName ? prev.phaseStartDate : new Date()) : null
    }));
  };

    // Session Mode explanation text
  const sessionModeExplanations = {
    continuous: "Therapy occurs as a continuous conversation without time-bounded sessions.",
    structured: "Therapy uses formal, scheduled sessions with specific start and end times.",
    hybrid: "Combines structured sessions with the ability to chat between sessions."
  };

  // Helper function to get platform default value (or fallback)
  const getDefaultValue = (field, fallback) => {
    // Use platformDefaults state
    switch (field) {
      case 'sessionLength':
        return clientSettings.sessionLength ?? platformDefaults.defaultSessionLength ?? fallback;
      case 'sessionFrequency':
        return clientSettings.sessionFrequency ?? platformDefaults.defaultSessionFrequency ?? fallback;
      case 'enforceTimeBoundaries':
        return clientSettings.enforceTimeBoundaries ?? platformDefaults.defaultEnforceTimeBoundaries ?? fallback;
      case 'reflectionFrequency':
        return clientSettings.customReflectionFrequency ?? platformDefaults.defaultReflectionFrequency ?? fallback;
      default:
        return fallback;
    }
  };

  // Function to render therapy progression visually
  const renderPathwayProgress = () => {
    if (!selectedPathwayId || !currentAssignedPathwayDetails) {
      return <Alert severity="info" sx={{mt: 2}}>No treatment pathway selected or details unavailable.</Alert>;
    }
    
    const pathway = currentAssignedPathwayDetails; // Use the detailed object
    
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Treatment Progress: {pathway.name}
        </Typography>
        
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>{pathway.name}</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {pathway.description}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              {pathway.phases && pathway.phases.map((phase, index) => { // Ensure phases exist
                const isCurrentPhase = phase.name === selectedPhaseName;
                // This logic for past phase might need adjustment based on how phase completion is tracked
                const currentPhaseIndex = pathway.phases.findIndex(p => p.name === selectedPhaseName);
                const isPastPhase = currentPhaseIndex > -1 && index < currentPhaseIndex;
                
                return (
                  <React.Fragment key={phase.name}>
                    <Chip 
                      icon={isPastPhase ? <CheckCircleIcon /> : isCurrentPhase ? <RadioButtonUncheckedIcon /> : undefined}
                      label={phase.name}
                      color={isCurrentPhase ? 'primary' : isPastPhase ? 'success' : 'default'}
                      variant={isCurrentPhase ? 'filled' : isPastPhase ? 'outlined' : 'outlined'}
                      sx={{ fontWeight: isCurrentPhase ? 'bold' : 'normal' }}
                    />
                    {index < pathway.phases.length - 1 && (
                      <ArrowForwardIcon color="action" fontSize="small" />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
            
            {selectedPhaseName && clientSettings.phaseStartDate && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Current phase started: {new Date(clientSettings.phaseStartDate).toLocaleDateString()}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  // Render Phase Transition Management panel
  const renderPhaseTransitionPanel = () => {
    if (!pathwayInfo?.pathway || !pathwayInfo?.currentPhase) {
      return null;
    }

    const { pendingTransition, currentPhase, nextPhase } = pathwayInfo;
    const hasNextPhase = nextPhase && nextPhase.requiresHumanApprovalToEnter;

    return (
      <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon fontSize="small" />
          Phase Transition Management
        </Typography>

        {/* Current Phase Info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Current Phase: <strong>{currentPhase.name}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI Autonomy Level: <Chip label={currentPhase.aiAutonomyLevel || 'full'} size="small" color={
              currentPhase.aiAutonomyLevel === 'full' ? 'success' :
              currentPhase.aiAutonomyLevel === 'supervised' ? 'warning' : 'error'
            } />
          </Typography>
        </Box>

        {/* Pending Transition Alert */}
        {pendingTransition && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            icon={<WarningIcon />}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  color="success"
                  variant="contained"
                  onClick={() => handleOpenTransitionDialog('approve')}
                  disabled={loadingTransition}
                >
                  Approve
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => handleOpenTransitionDialog('reject')}
                  disabled={loadingTransition}
                >
                  Reject
                </Button>
              </Box>
            }
          >
            <Typography variant="body2">
              <strong>Pending Phase Transition Request</strong>
            </Typography>
            <Typography variant="caption">
              Requested transition to: {pendingTransition.targetPhase}
              <br />
              Requested: {new Date(pendingTransition.requestedAt).toLocaleDateString()}
              {pendingTransition.notes && <><br />Notes: {pendingTransition.notes}</>}
            </Typography>
          </Alert>
        )}

        {/* Next Phase Readiness */}
        {hasNextPhase && !pendingTransition && readinessEvaluation && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Next Phase: {nextPhase.name}</strong>
              {nextPhase.requiresHumanApprovalToEnter && (
                <Chip label="Requires Your Approval" size="small" color="warning" sx={{ ml: 1 }} />
              )}
            </Typography>

            {/* Readiness Criteria */}
            {readinessEvaluation.criteria && readinessEvaluation.criteria.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Readiness Criteria:
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {readinessEvaluation.criteria.map((criterion, idx) => (
                    <Box key={criterion.criterionId || idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {criterion.met ? (
                        <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                      ) : (
                        <RadioButtonUncheckedIcon color="disabled" fontSize="small" sx={{ mr: 1 }} />
                      )}
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {criterion.description}
                      </Typography>
                      {criterion.evaluationType === 'therapist_attestation' && !criterion.met && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleAttestCriterion(criterion.criterionId)}
                          disabled={loadingTransition}
                        >
                          Attest
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Overall Readiness */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                Overall Readiness: {readinessEvaluation.isReady ? (
                  <Chip label="Ready for Transition" size="small" color="success" />
                ) : (
                  <Chip label="Not Yet Ready" size="small" color="default" />
                )}
              </Typography>
              {!readinessEvaluation.isReady && readinessEvaluation.blockers && (
                <Typography variant="caption" color="text.secondary">
                  Blockers: {readinessEvaluation.blockers.join(', ')}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* No next phase or at final phase */}
        {!hasNextPhase && !pendingTransition && (
          <Typography variant="body2" color="text.secondary">
            {nextPhase ? 'Next phase does not require manual approval.' : 'Client is in the final phase of this pathway.'}
          </Typography>
        )}
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading therapy settings...</Typography>
      </Box>
    );
  }
  // Error is handled by the main error Alert below

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>Client Therapy Settings</Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      
      <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Session Configuration</Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="session-mode-label">Session Mode</InputLabel>
              <Select
                labelId="session-mode-label"
                value={clientSettings.sessionMode || 'continuous'}
                onChange={(e) => handleGeneralSettingChange('sessionMode', e.target.value)}
                label="Session Mode"
              >
                <MenuItem value="continuous">Continuous</MenuItem>
                <MenuItem value="structured">Structured</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
              {sessionModeExplanations[clientSettings.sessionMode || 'continuous']}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="session-length-label">Session Length</InputLabel>
              <Select
                labelId="session-length-label"
                value={clientSettings.sessionLength ?? ''} // Use empty string for "Use Platform Default"
                onChange={(e) => handleGeneralSettingChange('sessionLength', e.target.value === '' ? null : e.target.value)}
                label="Session Length"
                disabled={clientSettings.sessionMode === 'continuous'}
              >
                <MenuItem value="">
                  <em>Use Platform Default ({platformDefaults.defaultSessionLength} min)</em>
                </MenuItem>
                <MenuItem value={30}>30 minutes</MenuItem>
                <MenuItem value={45}>45 minutes</MenuItem>
                <MenuItem value={60}>60 minutes</MenuItem>
                <MenuItem value={90}>90 minutes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="session-frequency-label">Session Frequency</InputLabel>
              <Select
                labelId="session-frequency-label"
                value={clientSettings.sessionFrequency ?? ''}
                onChange={(e) => handleGeneralSettingChange('sessionFrequency', e.target.value === '' ? null : e.target.value)}
                label="Session Frequency"
                disabled={clientSettings.sessionMode === 'continuous'}
              >
                <MenuItem value="">
                  <em>Use Platform Default ({platformDefaults.defaultSessionFrequency})</em>
                </MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="biweekly">Bi-weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={clientSettings.enforceTimeBoundaries === true} // Explicitly check for true
                  onChange={(e) => handleGeneralSettingChange('enforceTimeBoundaries', e.target.checked)}
                  disabled={clientSettings.sessionMode === 'continuous'}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography>Enforce Time Boundaries</Typography>
                  <Tooltip title="When enabled, sessions will strictly adhere to the set time limits.">
                    <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                  </Tooltip>
                </Box>
              }
              sx={{ mt: 3 }}
            />
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 2 }}>
          {clientSettings.sessionMode !== 'continuous' && clientSettings.nextScheduledSession && (
            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
              Next session scheduled: {new Date(clientSettings.nextScheduledSession).toLocaleString()}
            </Typography>
          )}
          {clientSettings.sessionMode !== 'continuous' && clientSettings.lastSessionDate && (
            <Typography variant="body2" color="text.secondary">
              Last session: {new Date(clientSettings.lastSessionDate).toLocaleString()}
            </Typography>
          )}
        </Box>
      </Paper>
      
      <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Treatment Pathway</Typography>
        
        <FormControl fullWidth margin="normal">
          <InputLabel id="treatment-pathway-label">Treatment Pathway</InputLabel>
          <Select
            labelId="treatment-pathway-label"
            value={selectedPathwayId}
            onChange={handlePathwaySelectionChange}
            label="Treatment Pathway"
          >
            <MenuItem value="">
              <em>None Selected</em>
            </MenuItem>
            {recommendedPathways.length > 0 && <ListSubheader>Recommended</ListSubheader>}
            {recommendedPathways.map(pathway => (
              <MenuItem key={pathway._id} value={pathway._id}>
                {pathway.name}
              </MenuItem>
            ))}
            {otherPathways.length > 0 && <ListSubheader>Other</ListSubheader>}
            {otherPathways.map(pathway => (
              <MenuItem key={pathway._id} value={pathway._id}>
                {pathway.name}
              </MenuItem>
            ))}
            {recommendedPathways.length === 0 && otherPathways.length === 0 && (
              <MenuItem disabled value="">
                <em>No pathways available</em>
              </MenuItem>
            )}
          </Select>
        </FormControl>
        
        {selectedPathwayId && (
          <FormControl fullWidth margin="normal">
            <InputLabel id="current-phase-label">Current Phase</InputLabel>
            <Select
              labelId="current-phase-label"
              value={selectedPhaseName}
              onChange={handlePhaseSelectionChange}
              label="Current Phase"
            >
              <MenuItem value="">
                <em>Select a phase</em>
              </MenuItem>
              {[...recommendedPathways, ...otherPathways]
                .find(p => p._id === selectedPathwayId)
                ?.phases.map(phaseName => (
                  <MenuItem key={phaseName} value={phaseName}>
                    {phaseName}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        )}
        
        {selectedPhaseName && ( // Show date picker if a phase is selected
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              Phase Start Date
            </Typography>
            <FallbackDatePicker
              label="Phase Start Date"
              value={clientSettings.phaseStartDate} // Ensure this is correctly bound
              onChange={(date) => handleGeneralSettingChange('phaseStartDate', date)}
            />
          </Box>
        )}
        
        {renderPathwayProgress()}
      </Paper>

      {/* Phase Transition Management Panel */}
      {renderPhaseTransitionPanel()}
      
      <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Reflection Settings</Typography>
        
        <FormControl fullWidth margin="normal">
          <InputLabel id="reflection-frequency-label">Reflection Frequency</InputLabel>
          <Select
            labelId="reflection-frequency-label"
            value={clientSettings.customReflectionFrequency ?? ''}
            onChange={(e) => handleGeneralSettingChange('customReflectionFrequency', e.target.value === '' ? null : e.target.value)}
            label="Reflection Frequency"
          >
            <MenuItem value={null}>
              <em>Use Platform Default ({getDefaultValue('reflectionFrequency', 'weekly')})</em>
            </MenuItem>
            <MenuItem value="after_session">After Each Session</MenuItem>
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>AI Therapist Settings</Typography>
        
        {/* Persona Selection */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="ai-persona-label">AI Therapist Persona</InputLabel>
          <Select
            labelId="ai-persona-label"
            value={selectedPersonaKey}
            onChange={(e) => setSelectedPersonaKey(e.target.value)}
            label="AI Therapist Persona"
          >
            {therapistPersonas.map(persona => (
              <MenuItem key={persona.key} value={persona.key}>
                {persona.name}
              </MenuItem>
            ))}
            {therapistPersonas.length === 0 && (
              <MenuItem value="generalist">General AI Therapist</MenuItem>
            )}
          </Select>
        </FormControl>
        
        {/* Start New Conversation Checkbox */}
        <FormControlLabel
          control={
            <Checkbox 
              checked={startNewConversation}
              onChange={(e) => setStartNewConversation(e.target.checked)}
            />
          }
          label="Start a new conversation when changing persona"
          sx={{ mt: 2, mb: 1 }}
        />
        
        {/* Apply Persona Change Button */}
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleApplyPersonaChange}
          disabled={personaSaving}
          sx={{ mt: 1, mb: 3 }}
        >
          {personaSaving ? <CircularProgress size={24} /> : 'Apply Persona Change'}
        </Button>
        
        {/* UI Control Toggles */}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Client UI Controls</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={clientSettings.clientUiOptions?.showPersonaSelector || false}
              onChange={(e) => handleUiOptionChange('showPersonaSelector', e.target.checked)}
            />
          }
          label="Allow client to select AI persona"
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={clientSettings.clientUiOptions?.showNewChatButton || false}
              onChange={(e) => handleUiOptionChange('showNewChatButton', e.target.checked)}
            />
          }
          label="Allow client to start new conversations"
          sx={{ display: 'block', mt: 1 }}
        />
        
        {/* Save UI Options Button */}
        <Button 
          variant="outlined" 
          color="primary"
          onClick={handleSaveUiOptions}
          disabled={personaSaving}
          sx={{ mt: 2 }}
        >
          Save UI Settings
        </Button>
      </Paper>
      
      {/* Voice Configuration - Now using enhanced multi-provider component */}
      <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3 }}>
        <ClientVoiceOverrides 
          token={token} 
          clientId={clientId} 
          clientName={clientName || 'Client'}
        />
      </Paper>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveSettings}
          disabled={saving}
          sx={{ minWidth: 150 }}
        >
          {saving ? <CircularProgress size={24} /> : 'Save Settings'}
        </Button>
      </Box>

      {/* Phase Transition Dialog */}
      <Dialog open={transitionDialogOpen} onClose={handleCloseTransitionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {transitionAction === 'approve' ? 'Approve Phase Transition' : 'Reject Phase Transition'}
        </DialogTitle>
        <DialogContent>
          {transitionAction === 'approve' && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              You are about to approve this client's transition to the next phase of their treatment pathway.
              This will grant the AI different autonomy levels as configured for the new phase.
            </Typography>
          )}
          {transitionAction === 'reject' && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Please provide a reason for rejecting this phase transition request.
              </Typography>
              <TextField
                fullWidth
                label="Rejection Reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
            </>
          )}
          <TextField
            fullWidth
            label="Additional Notes (optional)"
            value={transitionNotes}
            onChange={(e) => setTransitionNotes(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTransitionDialog} disabled={loadingTransition}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitTransition}
            variant="contained"
            color={transitionAction === 'approve' ? 'success' : 'error'}
            disabled={loadingTransition || (transitionAction === 'reject' && !rejectReason.trim())}
          >
            {loadingTransition ? <CircularProgress size={20} /> : (transitionAction === 'approve' ? 'Approve' : 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientTherapySettings;
